import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import withRouter from '../../../components/withRouter';
import TableContainer from '../../../components/TableContainerReactTable';
import ImportCampaignModal from '../components/campaign/ImportCampaignModal';
import { Campaign } from '../../../types/campaign';
import { format } from 'date-fns';
import { saveAs } from 'file-saver';
import { exportExcelCampaign, forceCacheUpdate, getCampaigns } from '../services/campaignApi';
import { getOrderStatusColor } from './Order';
import EditCampaignModal from '../components/campaign/EditCampaignModal';
import UpdateCampaignClickTagModal from '../components/campaign/UpdateCampaignClickTagModal';
import { toast } from 'react-toastify';
import { useProfile } from '../../(identity)/hooks/UserHooks';
import { useNavigate } from 'react-router-dom';
import BulkActionStatusCampaignModal from '../components/campaign/BulkActionStatusCampaignModal';
import { Loader2 } from 'lucide-react';

const Index = (props: any) => {
    useEffect(() => {
        getCampaigns().then(setData).finally(() => { setLoading(false); });
    }, []);

    const navigate = useNavigate();
    const { userProfile } = useProfile();
    const [data, setData] = useState<Campaign[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [isImportCampaignModalOpen, setIsImportCampaignModalOpen] = useState<boolean>(false);
    const [dataToEdit, setDataToEdit] = useState<Campaign | null>(null);
    const [isEditCampaignModalOpen, setIsEditCampaignModalOpen] = useState<boolean>(false);
    const [isUpdateCampaignClickTagModalOpen, setIsUpdateCampaignClickTagModalOpen] = useState<boolean>(false);
    const [isForceCacheUpdateLoading, setIsForceCacheUpdateLoading] = useState<boolean>(false);
    const [isBulkActionStatusCampaignModalOpen, setIsBulkActionStatusCampaignModalOpen] = useState<boolean>(false);

    const handleUpdateData = (newData: Campaign) => {
        const updatedData = data.map((item) => {
            if (item.id === newData.id) {
                return newData;
            }
            return item;
        });
        setData(updatedData);
    };

    const handleAddData = (newData: Campaign) => {
        setData([...data, newData]);
    };

    const toggleImportCampaignModal = () => {
        setIsImportCampaignModalOpen(!isImportCampaignModalOpen);
        updateCampaignList();
    };

    const toggleUpdateCampaignClickTagModal = () => {
        setIsUpdateCampaignClickTagModalOpen(!isUpdateCampaignClickTagModalOpen);
    };

    const toggleEditCampaignModal = () => {
        setIsEditCampaignModalOpen(!isEditCampaignModalOpen);
    };

    const toggleBulkActionStatusCampaignModal = () => {
        setIsBulkActionStatusCampaignModalOpen(!isBulkActionStatusCampaignModalOpen);
    };

    const updateCampaignList = () => {
        setLoading(true);
        getCampaigns().then(setData).finally(() => { setLoading(false); });
    };

    const forceCacheUpdateTrigger = () => {
        setIsForceCacheUpdateLoading(true);
        forceCacheUpdate().then(() => {
            toast.success('Cache atualizado com sucesso.');
        }).finally(() => { setIsForceCacheUpdateLoading(false); });
    };

    const statusTranslations: { [key: string]: string } = {
        DRAFT: 'Rascunho',
        NEEDS_CREATIVES: 'Necessita de Criativos',
        NEEDS_CLICK_TAG: 'Necessita de URL de Clique',
    };

    const columns = useMemo(
        () => [
            {
                header: 'ID',
                cell: (cell: any) => {
                    return (
                        <span className="fw-semibold">{cell.getValue()}</span>
                    );
                },
                accessorKey: 'id',
                enableColumnFilter: false,
                enableSorting: true,
            },
            {
                header: 'Nome',
                accessorKey: 'name',
                enableColumnFilter: false,
                enableSorting: true,
            },
            {
                header: 'Anunciante',
                accessorFn: (row: any) => {
                    return row.advertiser?.name || '-';
                },
                enableColumnFilter: false,
                enableSorting: true,
            },
            {
                header: 'Proteções',
                accessorKey: 'blockedKeywords.length',
                enableColumnFilter: false,
            },
            {
                header: 'Agência',
                accessorFn: (row: any) => {
                    return row.agency?.name || '-';
                },
                enableColumnFilter: false,
                enableSorting: true,
            },
            {
                header: 'Pedidos',
                cell: (cell: any) => {
                    const orders = cell.row.original.orders;
                    const statusCounts = orders.reduce((acc: any, order: any) => {
                        order.status.forEach((status: string) => {
                            acc[status] = (acc[status] || 0) + 1;
                        });
                        return acc;
                    }, {});

                    return Object.entries(statusCounts)
                        .map(([status, count]) => (
                            <>
                                <span
                                    key={status}
                                    className={`badge bg-${getOrderStatusColor(status)}`}
                                >{`${statusTranslations[status] ?? status} (${count})`}</span>
                                <br />
                            </>
                        ));
                },
                enableColumnFilter: false,
            },
            {
                header: 'GAM',
                cell: (cell: any) => {
                    const row = cell.row.original;
                    const syncStatus = row.gamDataSyncStatus;
                    const hasGamOrders = row.orders?.some((o: any) => o.gamOrderId);

                    if (!hasGamOrders && !syncStatus) return <span className="text-muted">-</span>;

                    const statusConfig: Record<string, { color: string; label: string }> = {
                        synced: { color: 'success', label: 'Sincronizado' },
                        pending: { color: 'warning', label: 'Pendente' },
                        no_data: { color: 'warning', label: 'Sem dados' },
                        error: { color: 'danger', label: 'Erro' },
                    };

                    const config = statusConfig[syncStatus] || { color: 'secondary', label: '-' };

                    return (
                        <span className={`badge bg-${config.color}`}>{config.label}</span>
                    );
                },
                enableColumnFilter: false,
            },
            {
                header: 'Data de Início',
                accessorFn: (row: any) => {
                    const campaigns = row.orders.sort(
                        (a: any, b: any) =>
                            new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
                    );
                    return campaigns[0]?.startDate
                        ? format(new Date(campaigns[0].startDate), 'dd/MM/yyyy')
                        : '-';
                },
                enableColumnFilter: false,
            },
            {
                header: 'Data de Termino',
                accessorFn: (row: any) => {
                    const campaigns = row.orders.sort(
                        (a: any, b: any) =>
                            new Date(b.endDate).getTime() - new Date(a.endDate).getTime()
                    );
                    return campaigns[0]?.endDate
                        ? format(new Date(campaigns[0].endDate), 'dd/MM/yyyy')
                        : '-';
                },
                enableColumnFilter: false,
            },
            {
                id: 'actions',
                cell: (cell: any) => {
                    const row = cell.row.original;
                    let actionCell = [];

                    actionCell.push(
                        <TooltipProvider key="action_report">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        className="btn text-primary d-inline-block"
                                        onClick={() => {
                                            navigate(`/campaign/report/${row.id}`);
                                        }}
                                    >
                                        <i className="ri-bar-chart-box-fill fs-16"></i>
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>Relatórios</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    );

                    if (userProfile?.role === 'ROLE_SUPER_ADMIN') {
                        actionCell.push(
                            <TooltipProvider key="action_edit">
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <button
                                            className="btn text-primary d-inline-block"
                                            onClick={() => {
                                                setDataToEdit(row);
                                                toggleEditCampaignModal();
                                            }}
                                        >
                                            <i className="ri-pencil-fill fs-16"></i>
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>Editar</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        );
                    }

                    actionCell.push(
                        <TooltipProvider key="action_export">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        className="btn text-success d-inline-block"
                                        onClick={() => {
                                            exportExcelCampaign(row.id)
                                                .then((res) => {
                                                    saveAs(res, `${row.id} - ${row.name}.xlsx`);
                                                })
                                                .catch(() => {
                                                });
                                        }}
                                    >
                                        <i className="ri-file-excel-2-fill fs-16"></i>
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>Exportar</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    );

                    if (userProfile?.role === 'ROLE_SUPER_ADMIN') {
                        actionCell.push(
                            <TooltipProvider key="action_update_click_tag">
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <button
                                            className="btn text-success d-inline-block"
                                            onClick={() => {
                                                setDataToEdit(row);
                                                toggleUpdateCampaignClickTagModal();
                                            }}
                                        >
                                            <i className="ri-link fs-16"></i>
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>Atualizar URL de clique</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        );
                    }

                    if (userProfile?.role === 'ROLE_SUPER_ADMIN') {
                        actionCell.push(
                            <TooltipProvider key="action_update_campaign_status">
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <button
                                            className="btn text-success d-inline-block"
                                            onClick={() => {
                                                setDataToEdit(row);
                                                toggleBulkActionStatusCampaignModal();
                                            }}
                                        >
                                            <i className="ri-loop-left-line fs-16"></i>
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>Atualização de status em massa</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        );
                    }

                    return <div className="flex gap-2">{actionCell}</div>;
                },
                enableColumnFilter: false,
                enableSorting: false,
            },
        ],
        [navigate, userProfile]
    );

    document.title = 'Campanhas | Ad Pulse';
    return (
        <React.Fragment>
            <div className="page-content">
                <div className="container-fluid">
                    <div className="grid gap-4">
                        <Card>
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div className="flex-1">
                                        <CardTitle>Campanhas</CardTitle>
                                    </div>
                                    {userProfile?.role === 'ROLE_SUPER_ADMIN' && (
                                        <div className="flex gap-2">
                                            <Button
                                                onClick={toggleImportCampaignModal}
                                                className="bg-green-600 hover:bg-green-700"
                                            >
                                                <i className="ri-add-line align-bottom mr-1"></i> Criar campanha
                                            </Button>
                                            <Button
                                                onClick={forceCacheUpdateTrigger}
                                                variant="secondary"
                                                disabled={isForceCacheUpdateLoading}
                                            >
                                                {isForceCacheUpdateLoading && (
                                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                )}
                                                Atualizar cache
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </CardHeader>
                            <CardContent>
                                {!loading ? (
                                    <TableContainer
                                        divClass="table-responsive"
                                        columns={(columns || [])}
                                        data={(data || [])}
                                        isGlobalFilter={true}
                                        customPageSize={30}
                                        SearchPlaceholder="Procurar..."
                                    />
                                ) : (
                                    <div className="flex justify-center">
                                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </div>

                {userProfile?.role === 'ROLE_SUPER_ADMIN' && (
                    <ImportCampaignModal
                        isOpen={isImportCampaignModalOpen}
                        toggle={toggleImportCampaignModal}
                    />
                )}

                {dataToEdit && userProfile?.role === 'ROLE_SUPER_ADMIN' && (
                    <EditCampaignModal
                        campaign={dataToEdit}
                        isOpen={isEditCampaignModalOpen}
                        toggle={toggleEditCampaignModal}
                        onUpdateData={handleUpdateData}
                    />
                )}

                {dataToEdit && userProfile?.role === 'ROLE_SUPER_ADMIN' && (
                    <UpdateCampaignClickTagModal
                        campaign={dataToEdit}
                        isOpen={isUpdateCampaignClickTagModalOpen}
                        toggle={toggleUpdateCampaignClickTagModal}
                        onUpdateData={handleUpdateData}
                    />
                )}

                {dataToEdit && userProfile?.role === 'ROLE_SUPER_ADMIN' && (
                    <BulkActionStatusCampaignModal
                        campaign={dataToEdit}
                        isOpen={isBulkActionStatusCampaignModalOpen}
                        toggle={toggleBulkActionStatusCampaignModal}
                        onUpdateData={handleUpdateData}
                    />
                )}
            </div>
        </React.Fragment>
    );
};

export default withRouter(Index);
